// OTP confirmation helpers (2026-08-14 confirmation hotfix). Email
// scanners pre-fetch links in incoming mail and consume single-use
// confirmation tokens before the human clicks; a 6-digit code typed
// by hand can't be consumed by a scanner. These helpers encode
// Supabase's ACTUAL responses, probed live against this project:
//
//   signUp, new email            → 200, identities.length ≥ 1, code emailed
//   signUp, existing UNCONFIRMED → 200, identities.length ≥ 1, code
//                                  auto-REsent (the unread-invite case)
//   signUp, existing UNCONFIRMED
//     within 60s of last email   → 429 over_email_send_rate_limit,
//                                  message carries the wait seconds
//   signUp, existing CONFIRMED   → 200, identities.length === 0
//                                  (obfuscated), nothing sent
//   resend, confirmed            → 200 silent no-op (enumeration
//                                  protection — NOT a usable signal)
//   signIn, unconfirmed          → 400 email_not_confirmed
//   verifyOtp, wrong/expired     → 403 otp_expired
export const RESEND_COOLDOWN_SECONDS = 60;

// "For security purposes, you can only request this after 58 seconds."
export function parseRetrySeconds(message: string | undefined): number | null {
  const m = /after (\d+) seconds?/.exec(message ?? "");
  return m ? Number(m[1]) : null;
}

export function isRateLimitError(message: string | undefined): boolean {
  const m = (message ?? "").toLowerCase();
  return m.includes("only request this after") || m.includes("rate limit");
}

export function isOtpExpiredError(message: string | undefined): boolean {
  const m = (message ?? "").toLowerCase();
  return m.includes("expired") || m.includes("invalid");
}

export function isEmailNotConfirmedError(message: string | undefined): boolean {
  return (message ?? "").toLowerCase().includes("not confirmed");
}

// Outcome of a signup attempt, mapped from the probe table above.
export type SignupOutcome =
  | { kind: "code-sent" } // new OR unconfirmed-existing: a code is in their inbox
  | { kind: "code-recently-sent"; retryAfter: number } // 429 inside the send window
  | { kind: "already-confirmed" } // identities === 0: finished account exists
  | { kind: "error"; message: string };

export function classifySignupResult(input: {
  errorMessage: string | null;
  identitiesLength: number | null; // null when no user object came back
  hasSession: boolean;
}): SignupOutcome | { kind: "signed-in" } {
  if (input.errorMessage) {
    if (isRateLimitError(input.errorMessage)) {
      return {
        kind: "code-recently-sent",
        retryAfter: parseRetrySeconds(input.errorMessage) ?? RESEND_COOLDOWN_SECONDS,
      };
    }
    return { kind: "error", message: input.errorMessage };
  }
  // Confirmations off (dev projects) hand back a session immediately.
  if (input.hasSession) return { kind: "signed-in" };
  if (input.identitiesLength === 0) return { kind: "already-confirmed" };
  return { kind: "code-sent" };
}
